// Fonte única de permissões do Portal — compartilhada entre o front (useCan, menu,
// meta.permissions) e o Nitro (requirePermission, mapa do proxy).
// Matriz canônica: docs/auth/permissions.md

export const PERMISSIONS = [
    'admin',
    'cs',
    'hunting',
    'consulting',
    'charges',
    'stakeholder',
    'marketing',
] as const

export type Permission = (typeof PERMISSIONS)[number]

// Quem trabalha com empresas: Empresas, App Jobs, tela Usuários, autocomplete de operador
export const OPERATIONAL: Permission[] = ['cs', 'hunting', 'consulting', 'charges']

// Quem opera serviços: página /services e fatura avulsa
export const SERVICES: Permission[] = ['hunting', 'consulting', 'charges']

// Quem cria, reseta e exclui workspaces de demonstração — página /demos (decisão-mestre 9 do roadmap
// da demo, worqspace/docs/features/demo/roadmap.md). `marketing` entra e não é OPERATIONAL, por isso a
// página é própria e não mora em Empresas
export const DEMO: Permission[] = ['cs', 'hunting', 'consulting', 'marketing']

// Quem envia Anúncios (e changelog) da Leadins para os clientes — página /announcements e mutações de
// post/**. CS e Marketing publicam avisos (decisão 2026-09-22; antes era só `marketing`). Pesquisas seguem só de `cs`
export const ANNOUNCEMENTS: Permission[] = ['cs', 'marketing']

export const PERMISSION_LABELS: Record<Permission, string> = {
    admin: 'Admin',
    cs: 'Customer Success',
    hunting: 'Hunting',
    consulting: 'Consultoria',
    charges: 'Financeiro',
    stakeholder: 'Stakeholder',
    marketing: 'Marketing',
}

export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
    admin: 'Superusuário: acesso total a tudo + gestão de usuários do Portal',
    cs: 'Empresas (atividades, canal de aquisição, VIP/Beta/Demo, limite de usuários, cliente e assinatura Asaas), Pesquisas, Anúncios e Dados Mensais',
    hunting: 'Operação de R&S: solicitações e vagas, cobrança extra e fatura avulsa',
    consulting: 'Consultoria de RH: solicitações, consultorias, vagas sob consultoria e faturamento',
    charges: 'Financeiro: Cobranças e NF, Asaas, gestão financeira de vagas e consultorias, Dados Mensais',
    stakeholder: 'Somente o dashboard completo (Dados Mensais e Detalhes do Serviço), sem operação',
    marketing: 'Anúncios e changelog da Leadins para os clientes, com acompanhamento de leituras',
}

// admin passa em qualquer checagem (bypass — decisão de produto).
// required vazio = basta estar logado.
export const hasPermission = (
    granted: readonly string[] | undefined | null,
    required: readonly string[],
) => {
    if (!granted?.length) return false
    if (granted.includes('admin')) return true
    if (!required?.length) return true
    return required.some((permission) => granted.includes(permission))
}

// ---------------------------------------------------------------------------
// Mapa (método, rota) → permissões do proxy api/admin/** — deny by default:
// rota do worqspace fora do mapa ⇒ 403 (rota nova exige entrada nova aqui).
// `action` é o slug gravado no AuditLog (só mutações); `check` é um guard fino
// por registro que o proxy roda antes de liberar (admin bypassa); `companyFrom`
// diz de onde derivar a empresa para o filtro do /log.
// ---------------------------------------------------------------------------

export interface AdminRoute {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE'
    pattern: string                 // segmentos, ':nome' captura (ex.: 'service/vacancy/:id/close')
    permissions: Permission[]       // [] = qualquer logado ativo
    action?: string                 // slug de auditoria — obrigatório nas mutações
    check?: 'intakeType' | 'vacancyCreate' | 'vacancyUpdate'
    companyFrom?: 'body.id' | 'body.companyId' | 'param.companyId' | 'param.id'
}

const ANYONE: Permission[] = []

// A ordem importa: padrões literais antes dos genéricos com :param
export const ADMIN_ROUTES: AdminRoute[] = [
    // --- Dashboard (cards "para todos") ---
    { method: 'GET', pattern: 'company/active', permissions: ANYONE },
    { method: 'GET', pattern: 'company/activity/live', permissions: ANYONE },
    { method: 'GET', pattern: 'service/count', permissions: ANYONE },
    { method: 'GET', pattern: 'monthly', permissions: ['stakeholder', 'cs', 'charges'] },
    // Linha do mês corrente calculada na hora (worqspace/docs/features/customerBase/roadmap.md, etapa 01.01) —
    // o mesmo público do registro, que já vê a composição do mês com nomes (DM-04)
    { method: 'GET', pattern: 'monthly/live', permissions: ['stakeholder', 'cs', 'charges'] },
    // Uso de IA da plataforma (worqspace/docs/copilot/04.01.plan.md, D22): tokens de IA por dia e por fonte, e quem
    // mais consumiu — o público dos Dados Mensais, porque é custo
    { method: 'GET', pattern: 'ai/usage', permissions: ['stakeholder', 'cs', 'charges'] },
    // Panorama dos canais de aquisição (worqspace/docs/features/customerBase/roadmap.md, etapa 02.02, CA-11): o
    // público dos Dados Mensais, porque mostra MRR — fora do OPERATIONAL dos demais GET de company/**
    { method: 'GET', pattern: 'company/acquisition/overview', permissions: ['stakeholder', 'cs', 'charges'] },
    // Panorama dos assinantes por ciclo e faixa (worqspace/docs/features/customerBase/roadmap.md, etapa 03.01, AP-08):
    // o público dos Dados Mensais, porque mostra MRR — fora do OPERATIONAL dos demais GET de company/**
    { method: 'GET', pattern: 'company/subscribers/overview', permissions: ['stakeholder', 'cs', 'charges'] },
    { method: 'GET', pattern: 'service/insights', permissions: ['stakeholder', 'charges'] },

    // --- Marketing: leituras largas (cards do dashboard), mutações estritas — Pesquisas = cs · Anúncios = cs|marketing ---
    { method: 'GET', pattern: 'survey', permissions: ANYONE },
    { method: 'GET', pattern: 'survey/:id', permissions: ANYONE },
    { method: 'GET', pattern: 'survey/:id/results', permissions: ANYONE },
    { method: 'POST', pattern: 'survey', permissions: ['cs'], action: 'survey.create' },
    { method: 'PUT', pattern: 'survey/:id', permissions: ['cs'], action: 'survey.update' },
    { method: 'DELETE', pattern: 'survey/:id', permissions: ['cs'], action: 'survey.delete' },
    { method: 'GET', pattern: 'post', permissions: ANYONE },
    { method: 'GET', pattern: 'post/:id', permissions: ANYONE },
    { method: 'GET', pattern: 'post/:id/reads', permissions: ANYONE },
    { method: 'POST', pattern: 'post', permissions: ANNOUNCEMENTS, action: 'post.create' },
    { method: 'PUT', pattern: 'post/:id', permissions: ANNOUNCEMENTS, action: 'post.update' },
    { method: 'DELETE', pattern: 'post/:id', permissions: ANNOUNCEMENTS, action: 'post.delete' },

    // --- Empresas: leituras OPERATIONAL (deliberadamente mais largas que a UI) ---
    { method: 'GET', pattern: 'company', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'company/list', permissions: [...OPERATIONAL, 'marketing'] }, // autocomplete de audiência dos Anúncios
    // Canais de aquisição — a lista fechada mora no worqspace (worqspace/docs/features/customerBase/roadmap.md, etapa
    // 02.01, CA-02): o filtro Canal, o cadastro rápido da lista e o cadastro no detalhe leem daqui
    { method: 'GET', pattern: 'company/acquisition/channels', permissions: OPERATIONAL },
    // Opções do filtro Plano da lista de Empresas (worqspace/docs/features/customerBase/roadmap.md, etapa 03.01, AP-08):
    // ciclos e faixas com quantos assinantes têm — sem MRR, leitura de todo OPERATIONAL
    { method: 'GET', pattern: 'company/subscribers/plans', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'company/users/:id', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'company/activity/:id', permissions: OPERATIONAL },
    // Uso de IA por empresa (worqspace/docs/copilot/04.01.plan.md, D22): semanas por fonte e a tabela por pessoa da
    // semana — leitura de todo operacional, como a atividade
    { method: 'GET', pattern: 'company/ai-usage/:id', permissions: OPERATIONAL },
    // Links de desconto (worqspace/docs/pricing P-09, A-09): gerar/revogar é de Financeiro e CS; ler, de todo operacional
    { method: 'GET', pattern: 'company/:id/discount-offer', permissions: OPERATIONAL },
    { method: 'POST', pattern: 'company/:id/discount-offer', permissions: ['charges', 'cs'], action: 'company.discountOffer', companyFrom: 'param.id' },
    { method: 'DELETE', pattern: 'company/:id/discount-offer/:code', permissions: ['charges', 'cs'], action: 'company.discountOfferRevoke', companyFrom: 'param.id' },
    { method: 'GET', pattern: 'company/:id', permissions: OPERATIONAL },
    { method: 'PUT', pattern: 'company/acquisition', permissions: ['cs', 'hunting', 'charges'], action: 'company.acquisition', companyFrom: 'body.id' },
    { method: 'PUT', pattern: 'company/vip', permissions: ['cs', 'hunting'], action: 'company.vip', companyFrom: 'body.id' },
    { method: 'PUT', pattern: 'company/beta', permissions: ['cs', 'hunting'], action: 'company.beta', companyFrom: 'body.id' },
    // Tornar/remover Demo: marca uma empresa de teste (criada à mão pelo time) como demo — worqspace/docs/features/demo/README.md
    { method: 'PUT', pattern: 'company/demo', permissions: ['cs', 'hunting'], action: 'company.demo', companyFrom: 'body.id' },
    { method: 'PUT', pattern: 'company/limit', permissions: ['cs', 'hunting'], action: 'company.limit', companyFrom: 'body.id' },
    { method: 'PUT', pattern: 'company/archive', permissions: OPERATIONAL, action: 'company.archive', companyFrom: 'body.id' },

    // --- App Jobs ---
    { method: 'GET', pattern: 'job', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'job/count', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'job/:id', permissions: OPERATIONAL },
    { method: 'PUT', pattern: 'job/premium', permissions: OPERATIONAL, action: 'job.premium' },
    { method: 'PUT', pattern: 'job/free', permissions: OPERATIONAL, action: 'job.free' },

    // --- Asaas: cadastrar/editar cliente e criar/alterar assinatura são de Financeiro e CS (decisão 2026-10-07;
    //     antes era só `charges`) — o CS fecha a assinatura direto no detalhe da empresa ---
    { method: 'GET', pattern: 'asaas/customer/:companyId', permissions: OPERATIONAL },
    { method: 'POST', pattern: 'asaas/customer/:companyId', permissions: ['charges', 'cs'], action: 'asaas.customerCreate', companyFrom: 'param.companyId' },
    { method: 'PUT', pattern: 'asaas/customer/:companyId', permissions: ['charges', 'cs'], action: 'asaas.customerUpdate', companyFrom: 'param.companyId' },
    { method: 'POST', pattern: 'asaas/customer/:companyId/associate', permissions: ['charges', 'cs'], action: 'asaas.customerAssociate', companyFrom: 'param.companyId' },
    { method: 'POST', pattern: 'asaas/subscription/:companyId', permissions: ['charges', 'cs'], action: 'asaas.subscription', companyFrom: 'param.companyId' },

    // --- Serviços: leituras ---
    { method: 'GET', pattern: 'service/config', permissions: SERVICES },
    { method: 'GET', pattern: 'service/intake', permissions: SERVICES },
    { method: 'GET', pattern: 'service/intake/:id', permissions: SERVICES },
    { method: 'GET', pattern: 'service/vacancy', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'service/vacancy/:id', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'service/consulting', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'service/consulting/:id', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'service/standalone', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'service/billing/snapshot/:companyId', permissions: SERVICES },
    { method: 'GET', pattern: 'service/billing/:companyId', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'service/agreement/:id/contract/download', permissions: OPERATIONAL },
    { method: 'GET', pattern: 'service/charge', permissions: ['charges'] },

    // --- Solicitações: gate pelo tipo do intake (guard fino confere o registro) ---
    { method: 'PUT', pattern: 'service/intake/:id', permissions: ['hunting', 'consulting'], action: 'intake.update', check: 'intakeType' },
    { method: 'POST', pattern: 'service/intake/:id/approve', permissions: ['hunting', 'consulting'], action: 'intake.approve', check: 'intakeType' },
    { method: 'POST', pattern: 'service/intake/:id/reject', permissions: ['hunting', 'consulting'], action: 'intake.reject', check: 'intakeType' },
    { method: 'POST', pattern: 'service/intake/:id/merge', permissions: ['hunting'], action: 'intake.merge', check: 'intakeType' }, // mesclar é só de vagas

    // --- Vagas ---
    { method: 'POST', pattern: 'service/vacancy', permissions: SERVICES, action: 'vacancy.create', check: 'vacancyCreate', companyFrom: 'body.companyId' },
    { method: 'PUT', pattern: 'service/vacancy/:id', permissions: SERVICES, action: 'vacancy.update', check: 'vacancyUpdate' },
    { method: 'POST', pattern: 'service/vacancy/:id/close', permissions: ['hunting', 'charges'], action: 'vacancy.close' },
    { method: 'POST', pattern: 'service/vacancy/:id/cancel', permissions: ['hunting', 'charges'], action: 'vacancy.cancel' },
    { method: 'POST', pattern: 'service/vacancy/:id/uncancel', permissions: SERVICES, action: 'vacancy.uncancel' },
    { method: 'POST', pattern: 'service/vacancy/:id/charge', permissions: ['hunting', 'charges'], action: 'vacancy.extraCharge' },

    // --- Consultorias / Avulsos / Faturamento ---
    { method: 'POST', pattern: 'service/consulting', permissions: ['consulting', 'charges'], action: 'consulting.create', companyFrom: 'body.companyId' },
    { method: 'POST', pattern: 'service/standalone', permissions: SERVICES, action: 'standalone.create', companyFrom: 'body.companyId' },
    { method: 'POST', pattern: 'service/billing/invoice', permissions: SERVICES, action: 'billing.invoice', companyFrom: 'body.companyId' },
    { method: 'DELETE', pattern: 'service/billing/invoice/:paymentId', permissions: SERVICES, action: 'billing.invoiceCancel' },
    { method: 'POST', pattern: 'service/billing/invoice/:paymentId/nf', permissions: ['charges'], action: 'billing.nf' },
    { method: 'PUT', pattern: 'service/charge/:id', permissions: SERVICES, action: 'charge.update' },

    // --- Demos: workspaces de demonstração (worqspace/docs/features/demo/README.md) ---
    { method: 'GET', pattern: 'demo', permissions: DEMO },
    { method: 'GET', pattern: 'demo/scenarios', permissions: DEMO },
    { method: 'GET', pattern: 'demo/:id', permissions: DEMO },
    { method: 'POST', pattern: 'demo', permissions: DEMO, action: 'demo.create' },
    { method: 'POST', pattern: 'demo/:id/reset', permissions: DEMO, action: 'demo.reset', companyFrom: 'param.id' },
    { method: 'DELETE', pattern: 'demo/:id', permissions: DEMO, action: 'demo.delete', companyFrom: 'param.id' },

    // --- Log de auditoria (tela /log; a escrita é server-to-server, fora do mapa) ---
    { method: 'GET', pattern: 'log', permissions: ['admin'] },

    // --- Contratos (reenviar/substituir = só charges; download é leitura OPERATIONAL acima) ---
    { method: 'POST', pattern: 'service/agreement/:id/contract', permissions: ['charges'], action: 'agreement.contractResend' },
    { method: 'POST', pattern: 'service/agreement/:id/contract/custom', permissions: ['charges'], action: 'agreement.contractCustom' },
    // snapshot fiscal do contrato (razão social/CNPJ/endereço/responsável legal) — mesmo
    // público que informa o responsável legal ao abrir vaga/consultoria
    { method: 'PUT', pattern: 'service/agreement/:id', permissions: SERVICES, action: 'agreement.billingUpdate' },
]

export interface MatchedAdminRoute extends AdminRoute {
    params: Record<string, string>
}

export const matchAdminRoute = (method: string, path: string): MatchedAdminRoute | null => {
    const segments = path.split('/').filter(Boolean)

    for (const route of ADMIN_ROUTES) {
        if (route.method !== method) continue

        const patternSegments = route.pattern.split('/')
        if (patternSegments.length !== segments.length) continue

        const params: Record<string, string> = {}
        let matched = true

        for (let i = 0; i < patternSegments.length; i++) {
            const patternSegment = patternSegments[i]!
            if (patternSegment.startsWith(':')) {
                params[patternSegment.slice(1)] = segments[i]!
            } else if (patternSegment !== segments[i]) {
                matched = false
                break
            }
        }

        if (matched) return { ...route, params }
    }

    return null
}

// Labels PT-BR dos slugs de ação — filtros da tela /log
export const ACTION_LABELS: Record<string, string> = {
    'company.acquisition': 'Canal de aquisição',
    'company.vip': 'VIP',
    'company.beta': 'Beta',
    'company.demo': 'Demo',
    'company.limit': 'Limite de usuários',
    'company.archive': 'Arquivar/desarquivar empresa',
    'company.discountOffer': 'Gerar link de desconto',
    'company.discountOfferRevoke': 'Revogar link de desconto',
    'job.premium': 'Job premium',
    'job.free': 'Job free',
    'asaas.customerCreate': 'Criar cliente Asaas',
    'asaas.customerUpdate': 'Editar cliente Asaas',
    'asaas.customerAssociate': 'Associar cliente Asaas',
    'asaas.subscription': 'Assinatura Asaas',
    'intake.update': 'Editar solicitação',
    'intake.merge': 'Mesclar solicitações',
    'intake.reject': 'Rejeitar solicitação',
    'intake.approve': 'Aprovar solicitação',
    'vacancy.create': 'Abrir vaga',
    'vacancy.close': 'Concluir vaga',
    'vacancy.cancel': 'Cancelar vaga',
    'vacancy.uncancel': 'Reabrir vaga',
    'vacancy.extraCharge': 'Cobrança extra',
    'vacancy.update': 'Editar vaga',
    'consulting.create': 'Iniciar consultoria',
    'standalone.create': 'Fatura avulsa',
    'billing.invoice': 'Faturar',
    'billing.invoiceCancel': 'Cancelar fatura Asaas',
    'billing.nf': 'Antecipar NF',
    'charge.update': 'Editar/cancelar cobrança',
    'agreement.contractResend': 'Reenviar contrato',
    'agreement.contractCustom': 'Substituir contrato',
    'survey.create': 'Criar pesquisa',
    'survey.update': 'Editar pesquisa',
    'survey.delete': 'Excluir pesquisa',
    'post.create': 'Criar anúncio',
    'post.update': 'Editar anúncio',
    'post.delete': 'Excluir anúncio',
    'demo.create': 'Criar demo',
    'demo.reset': 'Resetar demo',
    'demo.delete': 'Excluir demo',
    'god.create': 'Convidar usuário',
    'god.update': 'Editar usuário',
    'god.invite': 'Novo link de convite',
    'god.delete': 'Excluir convite',
    'god.onboarding': 'Definir nome',
    'god.passwordChange': 'Troca de senha',
}
